import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Target, 
  Calendar, 
  MapPin, 
  Users, 
  DollarSign,
  Upload,
  Link as LinkIcon,
  Save,
  Eye,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from 'axios';

const CreateProjectForm = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Get project ID from URL if editing
  const isEditMode = !!id;
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    location: '',
    beneficiaries: '',
    startDate: '',
    endDate: '',
    goalType: 'Fundraising',
    goalAmount: '',
    description: '',
    visibility: 'public',
    showOnHomepage: true,
    socialSharing: true,
    callToAction: 'Donate Now'
  });
  const [previewImage, setPreviewImage] = useState(null);
  
  // Fetch project data if in edit mode
  useEffect(() => {
    if (isEditMode) {
      const fetchProjectDetails = async () => {
        try {
          const token = localStorage.getItem('ngoToken');
          if (!token) {
            toast.error('Authentication required');
            navigate('/ngo/login');
            return;
          }
          
          // Try multiple possible endpoints to handle different server configurations
          const possibleEndpoints = [
            `http://localhost:3011/api/projects/${id}`,
            `http://localhost:5000/api/projects/${id}`,
            `https://bloodbackend-99n2.onrender.com/api/projects/${id}`
          ];
          
          let response = null;
          let error = null;
          
          // Try each endpoint until one works
          for (const endpoint of possibleEndpoints) {
            try {
              console.log(`Trying to fetch project details from endpoint: ${endpoint}`);
              
              response = await axios.get(endpoint, {
                headers: {
                  'Authorization': `Bearer ${token}`
                },
                timeout: 5000 // 5 second timeout
              });
              
              // If we get here, the request was successful
              console.log('Project details fetched successfully:', response.data);
              break;
            } catch (err) {
              console.log(`Endpoint ${endpoint} failed:`, err.message);
              error = err;
              // Continue to next endpoint
            }
          }
          
          // If we got a successful response, populate the form
          if (response && response.data) {
            const project = response.data;
            
            // Format dates for input fields (YYYY-MM-DD)
            const formatDate = (dateString) => {
              const date = new Date(dateString);
              return date.toISOString().split('T')[0];
            };
            
            setFormData({
              title: project.title || '',
              category: project.category || '',
              location: project.location || '',
              beneficiaries: project.beneficiaries || '',
              startDate: formatDate(project.startDate) || '',
              endDate: formatDate(project.endDate) || '',
              goalType: project.goalType || 'Fundraising',
              goalAmount: project.goalAmount || '',
              description: project.description || '',
              visibility: project.visibility || 'public',
              showOnHomepage: project.showOnHomepage !== false,
              socialSharing: project.socialSharing !== false,
              callToAction: project.callToAction?.buttonText || 'Donate Now'
            });
            
            if (project.image) {
              setPreviewImage(project.image);
            }
            
            setInitialLoading(false);
            return;
          }
          
          // If all endpoints failed, show error
          toast.error('Failed to load project details');
          navigate('/ngo/dashboard/projects');
        } catch (error) {
          console.error('Error fetching project details:', error);
          toast.error('Failed to load project details');
          navigate('/ngo/dashboard/projects');
        } finally {
          setInitialLoading(false);
        }
      };
      
      fetchProjectDetails();
    }
  }, [id, isEditMode, navigate]);

  const categories = [
    'Education', 
    'Health', 
    'Environment', 
    'Women Empowerment', 
    'Animal Welfare',
    'Children',
    'Elderly',
    'Disaster Relief',
    'Poverty Alleviation',
    'Arts & Culture',
    'Other'
  ];
  
  const goalTypes = [
    { value: 'Fundraising', label: '🎯 Fundraising' },
    { value: 'Volunteer Recruitment', label: '🤝 Volunteer Recruitment' },
    { value: 'Awareness Drive', label: '📣 Awareness Drive' }
  ];
  
  const callToActions = [
    'Donate Now',
    'Join as Volunteer',
    'Learn More',
    'Support Us',
    'Get Involved'
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e, isDraft = false) => {
    e.preventDefault();
    setLoading(true);
    
    // Basic form validation
    if (!formData.title || !formData.category || !formData.location) {
      toast.error('Please fill in all required fields (Title, Category, and Location)');
      setLoading(false);
      return;
    }
    
    try {
      // Get token from localStorage
      const token = localStorage.getItem('ngoToken');
      if (!token) {
        toast.error('Authentication required. Please login again.');
        navigate('/ngo/login');
        return;
      }

      // Format the data for the API
      const projectData = {
        ...formData,
        status: isDraft ? 'draft' : 'pending', // Changed from 'active' to 'pending' for new submissions
        image: previewImage || 'https://images.unsplash.com/photo-1559027615-cd4628902d4a?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
        callToAction: {
          buttonText: formData.callToAction,
          buttonLink: ''
        }
      };
      
      console.log(`${isEditMode ? 'Updating' : 'Creating'} project data:`, projectData);
      
      // Use a single primary endpoint for reliability
      const apiEndpoint = isEditMode
        ? `http://localhost:3011/api/projects/${id}`
        : 'http://localhost:3011/api/projects';
      
      console.log(`Submitting project to: ${apiEndpoint}`);
      console.log('Project data:', projectData);
      console.log('Using token:', token);
      
      try {
        let response;
        
        if (isEditMode) {
          // PUT request for updating
          response = await axios.put(apiEndpoint, projectData, {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            timeout: 10000 // 10 second timeout
          });
        } else {
          // POST request for creating
          response = await axios.post(apiEndpoint, projectData, {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            timeout: 10000 // 10 second timeout
          });
        }
        
        // If we get here, the request was successful
        console.log(`Project ${isEditMode ? 'updated' : 'saved'} successfully:`, response.data);
        
        // Show success message
        toast.success(isEditMode 
          ? (isDraft ? 'Project updated and saved as draft' : 'Project updated successfully') 
          : (isDraft ? 'Project saved as draft' : 'Project submitted for approval')
        );
        
        // Navigate back to projects list
        navigate('/ngo/dashboard/projects');
        return;
      } catch (error) {
        console.error(`Error ${isEditMode ? 'updating' : 'creating'} project:`, error);
        
        if (error.response) {
          console.error('Error response:', error.response.data);
          toast.error(`Failed to ${isEditMode ? 'update' : 'create'} project: ${error.response.data.msg || 'Server error'}`);
        } else if (error.request) {
          console.error('No response received');
          toast.error('Server not responding. Please try again later.');
        } else {
          console.error('Error setting up request:', error.message);
          toast.error(`Error: ${error.message}`);
        }
        
        setLoading(false);
      }
      
      // This section has been moved to the try block above
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} project:`, error);
      toast.error(`Failed to ${isEditMode ? 'update' : 'create'} project. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate('/ngo/dashboard/projects')}
          className="flex items-center text-blue-600 hover:text-blue-800"
        >
          <ArrowLeft size={16} className="mr-1" />
          Back to Projects
        </button>
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white mt-4 flex items-center">
          <Target className="mr-2" size={24} />
          {isEditMode ? 'Edit Project' : 'Create New Project / Campaign'}
        </h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          {isEditMode ? 'Update the details to edit the project' : 'Fill in the details to create a new project or fundraising campaign'}
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6">
        <form onSubmit={(e) => handleSubmit(e, false)}>
          <div className="space-y-6">
            {/* Project Details Section */}
            <div>
              <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">📝 Project Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="col-span-2">
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Project Title*
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    placeholder="Enter a clear, impactful name (e.g., Clean Water for Villages)"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>

                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Category / Cause*
                  </label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value="">Select a category</option>
                    {categories.map((category) => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="location" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Location*
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MapPin size={16} className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      required
                      placeholder="City/District/Region"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="beneficiaries" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Target Audience / Beneficiaries*
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Users size={16} className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="beneficiaries"
                      name="beneficiaries"
                      value={formData.beneficiaries}
                      onChange={handleChange}
                      required
                      placeholder="Who the project serves (e.g., rural women, school children)"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Start Date*
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Calendar size={16} className="text-gray-400" />
                    </div>
                    <input
                      type="date"
                      id="startDate"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleChange}
                      required
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    End Date*
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Calendar size={16} className="text-gray-400" />
                    </div>
                    <input
                      type="date"
                      id="endDate"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleChange}
                      required
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="goalType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Goal Type*
                  </label>
                  <select
                    id="goalType"
                    name="goalType"
                    value={formData.goalType}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    {goalTypes.map((type) => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                {formData.goalType === 'Fundraising' && (
                  <div>
                    <label htmlFor="goalAmount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Goal Amount (₹)*
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <DollarSign size={16} className="text-gray-400" />
                      </div>
                      <input
                        type="number"
                        id="goalAmount"
                        name="goalAmount"
                        value={formData.goalAmount}
                        onChange={handleChange}
                        required
                        min="1"
                        placeholder="Enter amount in INR"
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                  </div>
                )}

                <div className="col-span-2">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Project Description*
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    rows="5"
                    placeholder="Detailed explanation of the campaign's purpose, impact, methods, and current status"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  ></textarea>
                </div>
              </div>
            </div>

            {/* Media Upload Section */}
            <div>
              <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">📷 Media Upload</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Featured Banner Image
                  </label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                    {previewImage ? (
                      <div className="space-y-1 text-center">
                        <img src={previewImage} alt="Preview" className="mx-auto h-32 object-cover" />
                        <div className="flex text-sm text-gray-600">
                          <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none">
                            <span>Change image</span>
                            <input id="file-upload" name="file-upload" type="file" className="sr-only" onChange={handleImageChange} accept="image/*" />
                          </label>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-1 text-center">
                        <Upload className="mx-auto h-12 w-12 text-gray-400" />
                        <div className="flex text-sm text-gray-600">
                          <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none">
                            <span>Upload a file</span>
                            <input id="file-upload" name="file-upload" type="file" className="sr-only" onChange={handleImageChange} accept="image/*" />
                          </label>
                          <p className="pl-1">or drag and drop</p>
                        </div>
                        <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Call to Action Section */}
            <div>
              <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">💬 Call to Action</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="callToAction" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Button Text
                  </label>
                  <select
                    id="callToAction"
                    name="callToAction"
                    value={formData.callToAction}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    {callToActions.map((cta) => (
                      <option key={cta} value={cta}>{cta}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Visibility Settings */}
            <div>
              <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">✅ Visibility Settings</h2>
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    id="visibility-public"
                    name="visibility"
                    type="radio"
                    value="public"
                    checked={formData.visibility === 'public'}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <label htmlFor="visibility-public" className="ml-3 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Public (Visible to everyone)
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    id="visibility-private"
                    name="visibility"
                    type="radio"
                    value="private"
                    checked={formData.visibility === 'private'}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <label htmlFor="visibility-private" className="ml-3 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Private (Only visible to your organization)
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    id="showOnHomepage"
                    name="showOnHomepage"
                    type="checkbox"
                    checked={formData.showOnHomepage}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="showOnHomepage" className="ml-3 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Show on Homepage
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    id="socialSharing"
                    name="socialSharing"
                    type="checkbox"
                    checked={formData.socialSharing}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="socialSharing" className="ml-3 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Enable Social Sharing
                  </label>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={() => navigate('/ngo/dashboard/projects')}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={(e) => handleSubmit(e, true)}
                disabled={loading}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
              >
                <Save size={16} className="inline mr-1" />
                Save as Draft
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-blue-800 dark:hover:bg-blue-700"
              >
                {loading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {isEditMode ? 'Updating...' : 'Publishing...'}
                  </span>
                ) : (
                  <span>
                    <Eye size={16} className="inline mr-1" />
                    {isEditMode ? 'Update Project' : 'Publish Project'}
                  </span>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateProjectForm;
